import classNames from "classnames";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import AutoSuggest from "react-autosuggest";

import { ApiService, Icd10Code } from "pwapi";

import { ApiError } from "pwapi/core/ApiError";

import { SiteSpecificCodeReplacement } from "../DiagnosisDialog";

type ICD10InputProps = {
  icd10: string;
  icd10Error: boolean;
  setIcd10: Function;
  setIcd10Error: Function;
  placeholder?: string;
  siteSpecificCode?: SiteSpecificCodeReplacement;
};

const ICD10Input = (props: ICD10InputProps) => {
  const [icd10Codes, setIcd10Codes] = useState<Icd10Code[]>([]);
  const [suggestions, setSuggestions] = useState<Icd10Code[]>([]);
  const [dirty, setDirty] = useState(false);

  const fetchCodeList = async (text: string) => {
    const suggestions =
      (await getCodeListByCode(text)).concat(
        await getCodeListByDefinition(text)
      ) || [];
    setIcd10Codes(suggestions);
  };

  const getCodeListByDefinition = async (text: string) => {
    const search = await getIcd10Codes({ description: text });
    return search;
  };

  const getCodeListByCode = async (text: string, limit?: number) => {
    const search = await getIcd10Codes({ code: text.toUpperCase(), limit });
    return search;
  };

  const onSuggestionsFetchRequested = useCallback(
    debounce(
      ({ value }) => {
        fetchCodeList(value);
      },
      500,
      { leading: true, trailing: true }
    ),
    []
  );

  useEffect(() => {
    dirty && setError(props.icd10);
  }, [props.icd10, icd10Codes, dirty]);

  useEffect(() => {
    setSuggestions(icd10Codes);
  }, [icd10Codes]);

  useEffect(() => {
    const replacementCode = props.siteSpecificCode?.replacementCode?.code;
    if (replacementCode) {
      const fetchReplacementCode = async () => {
        try {
          // Fetch the full code to provide a "description" to the UI
          const results = await getCodeListByCode(replacementCode, 1);
          setDirty(false);
          setIcd10Codes(results);
        } catch (e) {
          if (!(e instanceof ApiError)) {
            throw e;
          }
          setIcd10Codes([]);
        }
      };
      fetchReplacementCode();
    } else {
      setIcd10Codes([]);
    }
  }, [props.siteSpecificCode?.replacementCode?.code]);

  const setError = useCallback(
    (text: string) => {
      props.setIcd10Error(getError(text, icd10Codes));
    },
    [icd10Codes]
  );

  const getError = (text: string, codeList: Icd10Code[]) =>
    text !== "" && !codeList.some((code: Icd10Code) => code.code === text);

  const getIcd10Codes = async (
    searchType: Parameters<typeof ApiService.listIcd10Codes>[0]
  ) => {
    const responseCodes = await ApiService.listIcd10Codes(searchType);

    return responseCodes.results || [];
  };

  const handleOnChange = (newValue: string) => {
    setDirty(true);
    props.setIcd10(newValue);
  };

  const isSiteSpecific =
    props.siteSpecificCode?.replacementCode?.code &&
    props.siteSpecificCode?.replacementCode?.code !==
      props.siteSpecificCode?.originalCode?.code &&
    props.icd10 === props.siteSpecificCode?.replacementCode?.code;

  const fullIcd10ReplacementCode = icd10Codes.find(
    (i) => i.code === props.siteSpecificCode?.replacementCode?.code
  );

  return (
    <div
      className={classNames("ICD10Input", {
        input__error: props.icd10Error,
        "input__error--top": props.icd10Error,
        "ICD10Input--siteSpecific": isSiteSpecific,
      })}
    >
      <label className="BillingCode__label" htmlFor="ICD10Input__input">
        ICD10
        <br />
        <AutoSuggest
          suggestions={suggestions}
          getSuggestionValue={(suggestion: Icd10Code) => suggestion.code}
          inputProps={{
            id: "ICD10Input__input",
            name: "ICD10Input__input",
            className: "ICD10Input__input",
            alt: "icd10-alt",
            placeholder: props.placeholder,
            value: props.icd10,
            onChange: (_, { newValue }) => {
              handleOnChange(newValue);
            },
          }}
          renderSuggestion={(suggestion) => (
            <div>
              <strong>{suggestion.code}</strong>
              <p>{suggestion.description}</p>
            </div>
          )}
          onSuggestionsClearRequested={() => {
            setSuggestions([]);
          }}
          onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        />
      </label>
      {isSiteSpecific && props.siteSpecificCode?.originalCode?.code && (
        <div className="ICD10Input__siteSpecific">
          {fullIcd10ReplacementCode?.description && (
            <strong>
              {fullIcd10ReplacementCode?.description}
              <br />
            </strong>
          )}
          (Made site-specific from {props.siteSpecificCode?.originalCode?.code})
        </div>
      )}
    </div>
  );
};

export default ICD10Input;
